/**
 * Översättningsmappar för rapport-vyn (S-08-04).
 *
 * Hard-rules från CLAUDE.md / UI-kontrakt vy 8:
 *  - Maskinvärden får aldrig visas i UI — alltid svensk översättning.
 *  - Status-mappning för admin (skiljer sig från walker): `new` -> "Ny",
 *    `handled` -> "Behandlad", `not_actionable` -> "Ej åtgärdbar".
 *  - Inga fritextfält — motiveringar kommer från fasta listor som
 *    speglar `ReportReasonCodes` i backend (Report.kt).
 *  - Default-filter: `new` + `handled` på, `not_actionable` av (admin
 *    vill primärt se aktivt arbete).
 */

export type ReportCategory = 'trasig_gatlampa' | 'klotter' | 'morkt_obehagligt' | 'sopor';
export type ReportStatus = 'new' | 'handled' | 'not_actionable';

export const ALL_CATEGORIES: readonly ReportCategory[] = [
  'trasig_gatlampa',
  'klotter',
  'morkt_obehagligt',
  'sopor',
] as const;

export const ALL_STATUSES: readonly ReportStatus[] = ['new', 'handled', 'not_actionable'] as const;

/** Default-filter enligt UI-kontrakt vy 8: aktivt arbete först. */
export const DEFAULT_STATUS_FILTER: readonly ReportStatus[] = ['new', 'handled'] as const;

/** Svensk etikett för kategori (samma för walker och admin). */
export function categoryLabel(category: ReportCategory): string {
  switch (category) {
    case 'trasig_gatlampa':
      return 'Trasig gatlampa';
    case 'klotter':
      return 'Klotter eller skadegörelse';
    case 'morkt_obehagligt':
      return 'Mörkt eller obehagligt';
    case 'sopor':
      return 'Sopor eller skräp';
  }
}

/**
 * Admin-mappning av status. Skiljer sig från walker-vyn där `new` visas
 * som "Mottagen" (bekräftande) — admin ser arbetslista-språk ("Ny").
 */
export function adminStatusLabel(status: ReportStatus): string {
  switch (status) {
    case 'new':
      return 'Ny';
    case 'handled':
      return 'Behandlad';
    case 'not_actionable':
      return 'Ej åtgärdbar';
  }
}

/**
 * Avvisa-motiveringar. Ska vara identisk lista med
 * `ReportReasonCodes.FOR_NOT_ACTIONABLE` i backend (Report.kt).
 */
export const REJECT_REASONS = [
  { code: 'dubbelrapport', label: 'Dubbelrapport' },
  { code: 'utanfor_omrade', label: 'Utanför Härden-området' },
  { code: 'ej_atgardbar', label: 'Ej åtgärdbar' },
  { code: 'redan_atgardad', label: 'Redan åtgärdad' },
  { code: 'oklart', label: 'Oklart vad som rapporteras' },
] as const;

export type RejectReasonCode = (typeof REJECT_REASONS)[number]['code'];

/**
 * Reset-motiveringar (markera som ny igen). Speglar
 * `ReportReasonCodes.FOR_NEW` i backend.
 */
export const REOPEN_REASONS = [
  { code: 'admin_reopened', label: 'Återöppnad för ny granskning' },
  { code: 'admin_correction', label: 'Korrigering av felaktig handling' },
] as const;

export type ReopenReasonCode = (typeof REOPEN_REASONS)[number]['code'];

/** Eko-motivering är hårdkodad i klienten enligt S-08-01-kontraktet. */
export const HANDLED_REASON_CODE = 'marked_handled' as const;
